import { Check, Laptop } from "lucide-react";
import { RobotDiagram } from "@/components/rc/RobotDiagram";
import { ControllerDiagram } from "@/components/rc/ControllerDiagram";
import { defaultConfig, drivetrainById, styleRoles } from "@/rc/drivetrains";
import "./landing.css";

const HEART = [1, 3, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 16, 17, 18, 22];
const CYCLE = [["tank2", "Tank"], ["car2", "Car"], ["xdrive", "X-drive"]];
const cycleDelay = (i) => ({ animationDelay: `${-((CYCLE.length - i) % CYCLE.length) * 2.4}s` });

const Stage = ({ children, testId }) => (
  <div className="relative flex h-40 items-center justify-center overflow-hidden rounded-2xl border bg-muted/40" data-testid={testId} aria-hidden="true">
    <div className="grid-bg absolute inset-0 opacity-60" />
    <div className="relative flex w-full items-center justify-center gap-3 px-5">{children}</div>
  </div>
);

const Signal = () => (
  <div className="relative h-6 flex-1">
    <div className="absolute inset-x-0 top-1/2 border-t-2 border-dashed border-primary/50" />
    <span className="signal-dot absolute top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent shadow-[0_0_10px] shadow-accent" />
  </div>
);

const MiniHub = () => (
  <div className="shrink-0 rounded-xl border-2 border-foreground/80 bg-background p-1.5 shadow-sm">
    <div className="grid grid-cols-5 gap-[3px]">
      {Array.from({ length: 25 }).map((_, i) => <span key={i} className={`hs-pixel h-2 w-2 rounded-[2px] ${HEART.includes(i) ? "is-heart" : ""}`} />)}
    </div>
    <span className="pulse-ring mx-auto mt-1.5 block h-2 w-2 rounded-full bg-primary" />
  </div>
);

export const FirmwareAnim = () => (
  <Stage testId="step-anim-firmware">
    <div className="flex w-full max-w-[240px] flex-col gap-4">
      <div className="flex items-center gap-2">
        <Laptop className="h-9 w-9 shrink-0 text-muted-foreground" strokeWidth={1.6} />
        <Signal />
        <MiniHub />
      </div>
      <div>
        <div className="relative h-4 text-[11px] font-semibold">
          <span className="hs-busy absolute inset-0 text-muted-foreground">Installing firmware…</span>
          <span className="hs-done absolute inset-0 origin-left text-[hsl(var(--success))]">Pybricks ready</span>
        </div>
        <div className="mt-1.5 flex items-center gap-2">
          <div className="h-2 flex-1 overflow-hidden rounded-full border bg-background"><div className="hs-fill h-full rounded-full bg-primary" /></div>
          <span className="hs-done flex h-5 w-5 items-center justify-center rounded-full bg-[hsl(var(--success))] text-white"><Check className="h-3 w-3" strokeWidth={3} /></span>
        </div>
      </div>
    </div>
  </Stage>
);

export const SetupAnim = () => (
  <Stage testId="step-anim-setup">
    <div className="flex flex-col gap-2">
      {CYCLE.map(([id, label], i) => (
        <span key={id} className="hs-chip rounded-full border px-3 py-1 text-xs font-semibold" style={cycleDelay(i)}>{label}</span>
      ))}
    </div>
    <div className="relative h-32 w-32">
      {CYCLE.map(([id], i) => (
        <div key={id} className="hs-cycle absolute inset-0" style={cycleDelay(i)}>
          <RobotDiagram dt={drivetrainById(id)} cfg={defaultConfig(id)} mode="ports" className="h-full w-full" />
        </div>
      ))}
    </div>
  </Stage>
);

export const DriveAnim = () => (
  <Stage testId="step-anim-drive">
    <ControllerDiagram roles={styleRoles("split_left", "tank")} marks={{ RT: "pos", LT: "neg" }} className="hs-live-pad w-28 shrink-0" />
    <Signal />
    <RobotDiagram dt={drivetrainById("tank2")} cfg={defaultConfig("tank2")} live className="h-24 w-24 shrink-0" />
  </Stage>
);
